/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { pipelinesService, type PipelineListParams } from '../../services/pipelines.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { ProjectCell } from '../../components/common/project-cell';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { PipelineWithId } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete, useHasPermission } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { PERMISSIONS } from '../../constants/permissions';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn, buildDefaultFilters, buildColumnFiltersParam } from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import { formatDate } from 'sharedUi/dateUtils';
import { StatusChip } from '../../components/common/panel';
import { artifactTone, runTone } from './pipeline-status';

const FILTER_DEFS: SaasColumn[] = [
  { field: 'pipelineCode', header: 'Pipeline Code', filter: true },
  { field: 'name', header: 'Name', filter: true },
  { field: 'status', header: 'Status', filter: true },
];

/** 40×40 keeps every row action above the desktop hit-area floor. */
const rowAction = 'ui-press flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-gray-700';

const PipelineList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({ entity: 'pipeline', basePath: '/pipelines' });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.PIPELINE) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.PIPELINE);
  const canDelete = useCanDelete(JOB_NAMES.PIPELINE);
  const canRun = useHasPermission(PERMISSIONS.PIPELINE_RUN_EXECUTE);

  const [rows, setRows] = useState<PipelineWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => { projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([])); }, []);

  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));
  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 10000 });
  }, []);

  const fetchRows = useCallback(
    async (page = 1, size = pageSize, sortBy = sortField, order = sortOrder, activeFilters = filters) => {
      try {
        setLoading(true);
        const params: PipelineListParams = { page, page_size: size, include_deleted: false };
        if (debouncedSearchText?.trim()) params.search = debouncedSearchText.trim();
        if (sortBy) { params.sort_by = sortBy; params.sort_order = order === 1 ? 'asc' : 'desc'; }
        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;
        if (selectedProjectId) params.app_id = selectedProjectId;
        params.include_global = showCore;
        const response = await pipelinesService.list(params);
        setRows((response.data || []).map((p) => ({ ...p, id: p._id })));
        setTotalRecords((response as any).pagination?.totalItems ?? (response as any).pagination?.total ?? response.total ?? 0);
      } catch (error) {
        showError(extractErrorMessage(error, 'Failed to load pipelines'));
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearchText, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]
  );

  const handlePageChange = (event: any) => { setFirst(event.first); setPageSize(event.rows); fetchRows(Math.floor(event.first / event.rows) + 1, event.rows, sortField, sortOrder); };
  const handleSort = (event: any) => { setSortField(event.sortField); setSortOrder(event.sortOrder); setFirst(0); fetchRows(1, pageSize, event.sortField, event.sortOrder); };
  const handleFilter = (event: any) => { const next = event.filters || {}; setFilters(next); setFirst(0); fetchRows(1, pageSize, sortField, sortOrder, next); };

  useEffect(() => { setFirst(0); fetchRows(1, pageSize); }, [debouncedSearchText, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;
    try {
      setDeleteLoading(true);
      await pipelinesService.softDelete(selectedId);
      setDeleteDialogOpen(false); setSelectedId(null);
      fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (error: any) {
      setDeleteDialogOpen(false); setSelectedId(null);
      showError(extractErrorMessage(error, 'Failed to delete pipeline'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRun = async (id: string) => {
    try {
      setRunningId(id);
      const run = await pipelinesService.run(id);
      toast.current?.show({ severity: 'success', summary: 'Run started', detail: run.pipelineRunId, life: 4000 });
      fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (error) {
      showError(extractErrorMessage(error, 'Failed to start the pipeline run'));
    } finally {
      setRunningId(null);
    }
  };

  // Spec §12.1: Code, Name, Status, Source Count, Last Run, Last Run Status, Latest Artifact, Updated At.
  const columns: SaasColumn<PipelineWithId>[] = useMemo(() => {
    const cols: SaasColumn<PipelineWithId>[] = [
      { field: 'pipelineCode', header: 'Pipeline Code', sortable: true, filter: true, hideable: false,
        body: (r) => <span className="font-mono text-xs text-gray-700 dark:text-gray-200">{r.pipelineCode}</span> },
      { field: 'name', header: 'Name', sortable: true, filter: true },
      { field: 'status', header: 'Status', sortable: true, filter: true, body: (r) => <StatusChip tone={r.status === 'ACTIVE' ? 'success' : 'neutral'}>{r.status}</StatusChip> },
      { field: 'sourceCount', header: 'Sources', align: 'right', body: (r) => <span className="tabular-nums">{r.sourceCount ?? 0}</span> },
      { field: 'lastRun', header: 'Last Run', body: (r) => (r.lastRun ? <span className="tabular-nums">{formatDate(r.lastRun.createdAt)}</span> : <span className="text-ink-muted">—</span>) },
      { field: 'lastRunStatus', header: 'Last Run Status', body: (r) => (r.lastRun?.status ? <StatusChip tone={runTone(r.lastRun.status)}>{r.lastRun.status}</StatusChip> : <span className="text-ink-muted">—</span>) },
      {
        field: 'latestArtifact', header: 'Latest Artifact',
        body: (r) => (r.latestArtifact ? (
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-xs tabular-nums text-gray-700 dark:text-gray-200">v{r.latestArtifact.artifactVersion}</span>
            <StatusChip tone={artifactTone(r.latestArtifact.status)}>{r.latestArtifact.status}</StatusChip>
          </div>
        ) : <span className="text-ink-muted">—</span>),
      },
      { field: 'updatedAt', header: 'Updated At', sortable: true, body: (r) => <span className="tabular-nums">{formatDate(r.updatedAt || r.createdAt)}</span> },
      { field: 'appId', header: 'Project', body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} /> },
      {
        field: 'actions', header: 'Actions', align: 'center', hideable: false,
        body: (r) => (
          <div className="flex justify-center gap-0.5">
            <button type="button" className={rowAction} onClick={() => navigateToView(r._id)} data-pr-tooltip="View" aria-label="View pipeline">
              <i className="pi pi-eye ui-act--view text-[1.05rem]" aria-hidden />
            </button>
            {canRun && r.status === 'ACTIVE' && (
              <button type="button" className={rowAction}
                      disabled={runningId === r._id || r.lastRun?.status === 'RUNNING' || r.lastRun?.status === 'QUEUED'}
                      onClick={() => handleRun(r._id)} data-pr-tooltip="Run" aria-label="Run pipeline">
                <i className={`pi ${runningId === r._id ? 'pi-spin pi-spinner' : 'pi-play'} ui-act--run text-[1.05rem]`} aria-hidden />
              </button>
            )}
            {canUpdate && (
              <button type="button" className={rowAction} onClick={() => navigateToEdit(r._id)} data-pr-tooltip="Edit" aria-label="Edit pipeline">
                <i className="pi pi-pencil ui-act--edit text-[1.05rem]" aria-hidden />
              </button>
            )}
            {canDelete && (
              <button type="button" className={rowAction} onClick={() => { setSelectedId(r._id); setDeleteDialogOpen(true); }} data-pr-tooltip="Delete" aria-label="Delete pipeline">
                <i className="pi pi-trash ui-act--delete text-[1.05rem]" aria-hidden />
              </button>
            )}
          </div>
        ),
      },
    ];
    return cols;
  }, [canUpdate, canDelete, canRun, runningId, accessibleProjects, navigateToView, navigateToEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PermissionGuard jobName={JOB_NAMES.PIPELINE}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-Pipeline-List">
        <PageHeader embedded={embedded} icon="pi pi-sitemap" title="Pipelines" subtitle="Ingestion recipes that turn sources into searchable artifacts" testId="Pipeline.Header" />
        <div className={embedded ? '' : 'ui-panel overflow-hidden'}>
          <SaasDataTable
            tableId="pipeline" columns={appId ? columns.filter((c) => c.field !== 'appId') : columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No pipelines found" first={first} rows={pageSize} totalRecords={totalRecords}
            onPage={handlePageChange} rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined} sortOrder={sortOrder || undefined} onSort={handleSort}
            filters={filters} onFilter={handleFilter}
            globalSearch={searchText} onGlobalSearchChange={setSearchText} globalSearchPlaceholder="Search pipelines..."
            testId="PipelineList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown value={selectedProjectId}
                            options={accessibleProjects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id }))}
                            onChange={(e: any) => setSelectedProjectId(e.value || null)} placeholder="Project" showClear style={{ minWidth: '160px' }} />
                )}
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="PipelineList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="whitespace-nowrap text-sm tabular-nums text-ink-muted">{loading && !rows.length ? 'loading\u2026' : `${totalRecords} ${totalRecords === 1 ? 'record' : 'records'}`}</span>
                <RefreshButton onClick={() => fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)} loading={loading} />
                {canCreate && <span className="ui-press inline-flex"><Button label="Create Pipeline" icon="pi pi-plus" onClick={() => navigateToCreate()} size="small" /></span>}
              </>
            }
          />
        </div>
        <DeleteConfirmDialog visible={deleteDialogOpen} onCancel={() => { setDeleteDialogOpen(false); setSelectedId(null); }}
                             onDelete={handleDeleteConfirm} message="Delete this pipeline? Its artifacts stay; a pipeline with a run in progress cannot be deleted."
                             testId="PipelineList.Dialog.Delete" loading={deleteLoading} />
      </div>
    </PermissionGuard>
  );
};

export default PipelineList;
